/**
 * Desktop Help menu and full-screen mobile menu.
 */

import { lockBodyScroll, unlockBodyScroll } from './modal-scroll-lock.js';

const desktopQuery = window.matchMedia('(min-width: 1280px)');

function isFocusableControl(target) {
	return target instanceof Element && Boolean(target.closest('a, button, input, textarea, select, summary, [tabindex]'));
}

function initPlaceholderLinks() {
	document.addEventListener('click', (event) => {
		const link = event.target instanceof Element ? event.target.closest('[data-dc-placeholder]') : null;
		if (!link) {
			return;
		}
		event.preventDefault();
	});

	// Runs after document listeners, so a popup script can open before the hash jump is cancelled.
	window.addEventListener('click', (event) => {
		const link = event.target instanceof Element ? event.target.closest('a[data-dc-popup]') : null;
		if (!link) {
			return;
		}
		event.preventDefault();
	});
}

function initHelpMenu() {
	const toggle = document.querySelector('[data-help-toggle]');
	const panel = document.getElementById('dc-help-menu');
	if (!(toggle instanceof HTMLButtonElement) || !panel) {
		return;
	}

	/** @type {ReturnType<typeof setTimeout> | null} */
	let closeTimer = null;

	const isDesktop = () => desktopQuery.matches;
	const isOpen = () => toggle.getAttribute('aria-expanded') === 'true';

	const clearCloseTimer = () => {
		if (closeTimer === null) {
			return;
		}
		clearTimeout(closeTimer);
		closeTimer = null;
	};

	const close = (returnFocus) => {
		clearCloseTimer();
		if (!isOpen()) {
			return;
		}
		toggle.setAttribute('aria-expanded', 'false');
		panel.hidden = true;
		if (returnFocus) {
			toggle.focus();
		}
	};

	const open = () => {
		clearCloseTimer();
		toggle.setAttribute('aria-expanded', 'true');
		panel.hidden = false;
	};

	const scheduleClose = () => {
		if (!isDesktop()) {
			return;
		}
		clearCloseTimer();
		closeTimer = setTimeout(() => close(false), 160);
	};

	toggle.addEventListener('click', () => {
		if (isOpen()) {
			close(false);
			return;
		}
		open();
	});

	// Desktop mega menu: open on hover; delay close so the pointer can reach the panel.
	toggle.addEventListener('mouseenter', () => {
		if (isDesktop()) {
			open();
		}
	});
	toggle.addEventListener('mouseleave', scheduleClose);
	panel.addEventListener('mouseenter', () => {
		if (isDesktop()) {
			open();
		}
	});
	panel.addEventListener('mouseleave', scheduleClose);

	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape' && isOpen()) {
			event.preventDefault();
			close(true);
		}
	});

	document.addEventListener('click', (event) => {
		if (!isOpen() || !(event.target instanceof Node)) {
			return;
		}
		if (toggle.contains(event.target) || panel.contains(event.target)) {
			return;
		}
		close(!isFocusableControl(event.target));
	});

	desktopQuery.addEventListener('change', () => close(false));
}

function focusableItems(root) {
	return [...root.querySelectorAll('a, button, input, select, textarea, [tabindex]')].filter((element) => {
		if (!(element instanceof HTMLElement) || element.hidden) {
			return false;
		}
		return !element.closest('[hidden]');
	});
}

function initMobileMenu() {
	const toggle = document.querySelector('[data-mobile-menu-toggle]');
	const nav = document.getElementById('dc-mobile-nav');
	if (!(toggle instanceof HTMLButtonElement) || !nav) {
		return;
	}

	const closeButton = nav.querySelector('[data-mobile-menu-close]');
	const helpToggle = nav.querySelector('[data-mobile-help-toggle]');
	const helpPanel = document.getElementById('dc-mobile-help');
	let open = false;

	const close = (returnFocus) => {
		if (!open) {
			return;
		}
		open = false;
		nav.hidden = true;
		toggle.setAttribute('aria-expanded', 'false');
		unlockBodyScroll();
		if (returnFocus) {
			toggle.focus();
		}
	};

	const show = () => {
		if (open) {
			return;
		}
		open = true;
		nav.hidden = false;
		toggle.setAttribute('aria-expanded', 'true');
		lockBodyScroll();
		if (closeButton instanceof HTMLElement) {
			closeButton.focus();
		}
	};

	toggle.addEventListener('click', () => {
		if (open) {
			close(false);
			return;
		}
		show();
	});

	closeButton?.addEventListener('click', () => close(true));

	helpToggle?.addEventListener('click', () => {
		if (!(helpToggle instanceof HTMLButtonElement) || !helpPanel) {
			return;
		}
		const expanded = helpToggle.getAttribute('aria-expanded') === 'true';
		helpToggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
		helpPanel.hidden = expanded;
	});

	nav.addEventListener('keydown', (event) => {
		if (!open) {
			return;
		}
		if (event.key === 'Escape') {
			event.preventDefault();
			close(true);
			return;
		}
		if (event.key !== 'Tab') {
			return;
		}
		const items = focusableItems(nav);
		if (!items.length) {
			return;
		}
		const first = items[0];
		const last = items[items.length - 1];
		if (event.shiftKey && document.activeElement === first) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && document.activeElement === last) {
			event.preventDefault();
			first.focus();
		}
	});

	desktopQuery.addEventListener('change', (event) => {
		if (event.matches) {
			close(false);
		}
	});
}

export function initHeaderMenu() {
	initPlaceholderLinks();
	initHelpMenu();
	initMobileMenu();
}
